import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { AssetStore } from './assets.ts';
import { PhotoStrip } from './PhotoStrip.tsx';
import type { Element, Op, Project, PropValue, Rotation, Sheet, Wire } from '@elec/domain';
import type { Editor } from '@elec/editor';
import type { Locale, MessageKey, Translate } from '@elec/i18n';
import { getSymbol, type FieldDef } from '@elec/symbols';
import { catalogKind, findRef, matchesFor, refKey } from '@elec/catalog';
import { SymbolIcon } from './SymbolIcon.tsx';
import { IconButton } from './ui.tsx';

/** Text input that commits on blur or Enter (one undo step per edit, not per keystroke). */
function CommitInput(props: {
  value: string;
  onCommit(v: string): void;
  type?: 'text' | 'number';
  step?: number;
  min?: number;
  placeholder?: string;
  invalid?: boolean;
  id?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  multiline?: boolean;
}) {
  const [v, setV] = useState(props.value);
  useEffect(() => setV(props.value), [props.value]);
  const commit = () => {
    if (v !== props.value) props.onCommit(v);
  };
  if (props.multiline)
    return (
      <textarea
        id={props.id}
        value={v}
        rows={3}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setV(props.value);
        }}
      />
    );
  return (
    <input
      id={props.id}
      ref={props.inputRef}
      type={props.type ?? 'text'}
      value={v}
      step={props.step}
      min={props.min}
      placeholder={props.placeholder}
      aria-invalid={props.invalid || undefined}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setV(props.value);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function Field({
  def,
  value,
  locale,
  onChange,
}: {
  def: FieldDef;
  value: PropValue | undefined;
  locale: Locale;
  onChange(v: PropValue): void;
}) {
  const id = `f-${def.key}`;
  const label = (
    <label htmlFor={id}>
      {def.label[locale]}
      {'unit' in def && def.unit ? <span className="unit"> ({def.unit})</span> : null}
    </label>
  );
  const current = value ?? ('default' in def ? def.default : undefined);
  switch (def.type) {
    case 'select':
      return (
        <div className="field">
          {label}
          <select
            id={id}
            value={String(current ?? '')}
            onChange={(e) => {
              const opt = def.options.find((o) => String(o.value) === e.target.value);
              if (opt) onChange(opt.value);
            }}
          >
            {def.options.map((o) => (
              <option key={String(o.value)} value={String(o.value)}>
                {o.label?.[locale] ?? String(o.value)}
              </option>
            ))}
          </select>
        </div>
      );
    case 'boolean':
      return (
        <div className="field check">
          <label>
            <input type="checkbox" checked={!!current} onChange={(e) => onChange(e.target.checked)} />{' '}
            {def.label[locale]}
          </label>
        </div>
      );
    case 'number':
      return (
        <div className="field">
          {label}
          <CommitInput
            id={id}
            type="number"
            step={def.step ?? 1}
            min={def.min}
            value={current === undefined ? '' : String(current)}
            onCommit={(v) => onChange(v === '' ? '' : Number(v))}
          />
        </div>
      );
    case 'text':
      return (
        <div className="field">
          {label}
          <CommitInput
            id={id}
            value={String(current ?? '')}
            onCommit={onChange}
            multiline={def.key === 'text'}
          />
        </div>
      );
  }
}

export interface InspectorHandle {
  focusFirst(): void;
}

interface Props {
  /** Which section of the sidebar: what is selected, or the sheet (and its plan background). */
  part: 'selection' | 'sheet';
  editor: Editor;
  project: Project;
  sheet: Sheet;
  locale: Locale;
  t: Translate;
  onProjectInfo(): void;
  onImportBackground(): void;
  onCalibrate(): void;
  /** Photos of the selected device or point (project assets). */
  assets: AssetStore;
  notify(msg: string): void;
}

/** Manufacturer reference: the catalogue lines that match the device's characteristics. */
function CatalogField({
  el,
  t,
  onChange,
}: {
  el: Element;
  t: Translate;
  onChange(props: Element['props']): void;
}) {
  const matches = matchesFor(el);
  const current = findRef(el.props.catalogRef);
  const options = current && !matches.includes(current) ? [current, ...matches] : matches;
  return (
    <div className="field">
      <label htmlFor="f-ref">{t('cat.ref')}</label>
      <select
        id="f-ref"
        value={current ? refKey(current) : ''}
        onChange={(e) => {
          const { catalogRef: _old, ...rest } = el.props;
          onChange(e.target.value ? { ...rest, catalogRef: e.target.value } : rest);
        }}
        data-testid="catalog-ref"
      >
        <option value="">{matches.length ? t('cat.none') : t('cat.noMatch')}</option>
        {options.map((c) => (
          <option key={refKey(c)} value={refKey(c)}>
            {c.brand} {c.reference} — {c.description}
          </option>
        ))}
      </select>
      {current && (
        <p className="small muted">
          {current.range}
          {current.modules ? ` · ${t('cat.modules', { n: current.modules })}` : ''} ·{' '}
          <a href={current.sourceUrl} target="_blank" rel="noreferrer">
            {t('cat.source')}
          </a>
          {current && !matches.includes(current) && <span className="warn"> · {t('cat.mismatch')}</span>}
        </p>
      )}
    </div>
  );
}

export const Inspector = forwardRef<InspectorHandle, Props>(function Inspector(
  { part, editor, project, sheet, locale, t, onProjectInfo, onImportBackground, onCalibrate, assets, notify },
  ref,
) {
  const firstInput = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => ({
    focusFirst: () => {
      const el = root.current?.querySelector<HTMLElement>('textarea, input, select');
      el?.focus();
      if (el instanceof HTMLInputElement) el.select();
    },
  }));
  const ids = [...editor.selection];
  const elements = ids.map((id) => project.elements[id]).filter((e): e is Element => !!e);
  const wires = ids.map((id) => project.wires[id]).filter((w): w is Wire => !!w);
  const update = (ops: Op[]) => editor.update(ops);

  let body: React.ReactNode;
  if (part === 'selection' && ids.length === 0) {
    body = (
      <>
        <p className="muted">{t('panel.noSelection')}</p>
        <p className="muted small">{t('side.selectHint')}</p>
      </>
    );
  } else if (part === 'sheet') {
    body = null; // below
  } else if (elements.length === 1 && wires.length === 0) {
    const el = elements[0]!;
    const def = getSymbol(el.symbolId);
    // A point code is repeated on purpose between the single-line diagram and the plan;
    // contacts share their relay's tag. Only flag duplicates among sheets of the same kind.
    const kindOf = (sheetId: string) => project.sheets[sheetId]?.kind;
    const dupTag =
      !!el.tag &&
      def?.category !== 'control' &&
      Object.values(project.elements).some(
        (o) =>
          o.id !== el.id &&
          o.tag === el.tag &&
          kindOf(o.sheetId) === kindOf(el.sheetId) &&
          getSymbol(o.symbolId)?.category !== 'control',
      );
    body = (
      <>
        <div className="symbol-head">
          {def && <SymbolIcon def={def} size={40} />}
          <strong>{def?.name[locale] ?? el.symbolId}</strong>
        </div>
        {!def?.freeText && !def?.room && (
          <div className="field">
            <label htmlFor="f-tag">{t('panel.tag')}</label>
            <CommitInput
              id="f-tag"
              inputRef={firstInput}
              value={el.tag}
              invalid={dupTag}
              onCommit={(v) =>
                update([{ op: 'update', coll: 'elements', id: el.id, patch: { tag: v.trim() } }])
              }
            />
            {dupTag && <p className="warn">{t('panel.tagDuplicate')}</p>}
          </div>
        )}
        {def?.fields?.map((f) => (
          <Field
            key={f.key}
            def={f}
            locale={locale}
            value={el.props[f.key]}
            onChange={(v) =>
              update([
                { op: 'update', coll: 'elements', id: el.id, patch: { props: { ...el.props, [f.key]: v } } },
              ])
            }
          />
        ))}
        {catalogKind(el) && (
          <CatalogField
            el={el}
            t={t}
            onChange={(props) => update([{ op: 'update', coll: 'elements', id: el.id, patch: { props } }])}
          />
        )}
        <div className="field-row">
          <div className="field">
            <label htmlFor="f-rot">{t('panel.rotation')}</label>
            <select
              id="f-rot"
              value={el.rotation}
              onChange={(e) =>
                update([
                  {
                    op: 'update',
                    coll: 'elements',
                    id: el.id,
                    patch: { rotation: Number(e.target.value) as Rotation },
                  },
                ])
              }
            >
              {[0, 90, 180, 270].map((r) => (
                <option key={r} value={r}>
                  {r}°
                </option>
              ))}
            </select>
          </div>
          <div className="field check">
            <label>
              <input
                type="checkbox"
                checked={el.mirrored}
                onChange={(e) =>
                  update([
                    { op: 'update', coll: 'elements', id: el.id, patch: { mirrored: e.target.checked } },
                  ])
                }
              />{' '}
              {t('panel.mirrored')}
            </label>
          </div>
        </div>
        {!def?.freeText && !def?.room && (
          <div className="field" data-testid="element-photos">
            <label>{t('panel.photos')}</label>
            <PhotoStrip
              t={t}
              assets={assets}
              photos={el.photos ?? []}
              title={el.tag || (def?.name[locale] ?? '')}
              readOnly={editor.history.readOnly}
              notify={notify}
              noCamera={!window.matchMedia?.('(pointer: coarse)').matches}
              testId="element-photo-strip"
              onChange={(photos) =>
                update([
                  {
                    op: 'update',
                    coll: 'elements',
                    id: el.id,
                    patch: { photos: photos.length ? photos : undefined },
                  },
                ])
              }
            />
          </div>
        )}
        <p className="muted small">
          {t('panel.position')} : {el.x.toLocaleString(locale)} × {el.y.toLocaleString(locale)}
        </p>
      </>
    );
  } else if (wires.length === 1 && elements.length === 0) {
    const w = wires[0]!;
    body = (
      <>
        <div className="symbol-head">
          <strong>{t('panel.wire')}</strong>
        </div>
        <div className="field">
          <label htmlFor="f-wire">{t('panel.wireLabel')}</label>
          <CommitInput
            id="f-wire"
            inputRef={firstInput}
            value={w.label ?? ''}
            placeholder={t('panel.wireLabelHint')}
            onCommit={(v) =>
              update([{ op: 'update', coll: 'wires', id: w.id, patch: { label: v.trim() || undefined } }])
            }
          />
        </div>
      </>
    );
  } else if (ids.length > 1) {
    body = (
      <>
        <p>{t('panel.multi', { n: ids.length })}</p>
        <div className="row-buttons">
          <IconButton icon="rotate" label={t('tool.rotate')} shortcut="R" onClick={() => editor.rotate()} />
          <IconButton icon="mirror" label={t('tool.mirror')} shortcut="M" onClick={() => editor.mirror()} />
          <IconButton
            icon="delete"
            label={t('tool.delete')}
            shortcut="Suppr"
            onClick={() => editor.deleteSelection()}
          />
        </div>
      </>
    );
  }
  if (part === 'sheet') {
    const patch = (p: Partial<Sheet>) => update([{ op: 'update', coll: 'sheets', id: sheet.id, patch: p }]);
    body = (
      <>
        <div className="field">
          <label htmlFor="s-name">{t('panel.sheetName')}</label>
          <CommitInput
            id="s-name"
            value={sheet.name}
            onCommit={(v) => v.trim() && patch({ name: v.trim() })}
          />
        </div>
        <div className="field">
          <label htmlFor="s-kind">{t('panel.sheetKind')}</label>
          <select
            id="s-kind"
            value={sheet.kind}
            onChange={(e) => patch({ kind: e.target.value as Sheet['kind'] })}
          >
            {(['single-line', 'multi-line', 'control', 'position-plan'] as const).map((k) => (
              <option key={k} value={k}>
                {t(`sheetKind.${k}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="s-paper">{t('panel.paper')}</label>
            <select
              id="s-paper"
              value={sheet.paper}
              onChange={(e) => patch({ paper: e.target.value as Sheet['paper'] })}
            >
              <option value="A4">A4</option>
              <option value="A3">A3</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="s-or">{t('panel.orientation')}</label>
            <select
              id="s-or"
              value={sheet.orientation}
              onChange={(e) => patch({ orientation: e.target.value as Sheet['orientation'] })}
            >
              <option value="landscape">{t('panel.landscape')}</option>
              <option value="portrait">{t('panel.portrait')}</option>
            </select>
          </div>
        </div>
        <button className="wide-btn" onClick={onProjectInfo}>
          {t('menu.projectInfo')}…
        </button>
        {sheet.kind === 'position-plan' && (
          <>
            <h2>{t('panel.background')}</h2>
            {sheet.background ? (
              <>
                <div className="field">
                  <label htmlFor="bg-op">
                    {t('plan.opacity')} ({Math.round(sheet.background.opacity * 100)} %)
                  </label>
                  <input
                    id="bg-op"
                    type="range"
                    min={0.1}
                    max={1}
                    step={0.05}
                    value={sheet.background.opacity}
                    onChange={(e) =>
                      patch({ background: { ...sheet.background!, opacity: Number(e.target.value) } })
                    }
                  />
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>X (mm)</label>
                    <CommitInput
                      type="number"
                      value={String(Math.round(sheet.background.x))}
                      onCommit={(v) => patch({ background: { ...sheet.background!, x: Number(v) } })}
                    />
                  </div>
                  <div className="field">
                    <label>Y (mm)</label>
                    <CommitInput
                      type="number"
                      value={String(Math.round(sheet.background.y))}
                      onCommit={(v) => patch({ background: { ...sheet.background!, y: Number(v) } })}
                    />
                  </div>
                </div>
                <p className="muted small">
                  {t('plan.scale')}
                  {sheet.planScale ?? 50}
                </p>
                <button className="wide-btn" onClick={onCalibrate} data-testid="calibrate">
                  {t('plan.calibrate')}
                </button>
                <button className="wide-btn" onClick={onImportBackground}>
                  {t('plan.replace')}
                </button>
                <button className="wide-btn" onClick={() => patch({ background: undefined })}>
                  {t('plan.remove')}
                </button>
              </>
            ) : (
              <>
                <p className="muted small">{t('plan.noBackground')}</p>
                <button className="wide-btn" onClick={onImportBackground} data-testid="import-background">
                  {t('plan.import')}
                </button>
              </>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <div className="inspector-body" ref={root}>
      {body}
    </div>
  );
});
